import type { ActiveSession } from '@nexo/types';
import { Button, Spinner, useToast } from '@nexo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, Laptop, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { keys } from '../../lib/queries';
import { errorMessage } from '../../lib/forms';

const icons = { desktop: Laptop, mobile: Smartphone, web: Globe } as const;

/** Devices where the user is signed in, each one can be signed out from here. */
export function ActiveSessions() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const sessions = useQuery({ queryKey: keys.sessions, queryFn: api.auth.sessions });
  const onError = (error: unknown) =>
    toast({
      title: t('No se pudo cerrar la sesión'),
      description: errorMessage(error),
      tone: 'error',
    });
  const onSuccess = () => queryClient.invalidateQueries({ queryKey: keys.sessions });
  const revoke = useMutation({ mutationFn: api.auth.revokeSession, onSuccess, onError });
  const revokeOthers = useMutation({
    mutationFn: api.auth.revokeOtherSessions,
    onSuccess,
    onError,
  });

  const list = sessions.data ?? [];
  const others = list.filter((s) => !s.current);

  return (
    <section className="mt-4 rounded-2xl border border-border bg-surface p-6 shadow-panel">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">{t('Sesiones activas')}</h2>
        {others.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            loading={revokeOthers.isPending}
            onClick={() => {
              if (window.confirm(t('¿Cerrar la sesión en todos los demás dispositivos?')))
                revokeOthers.mutate();
            }}
          >
            {t('Cerrar las demás')}
          </Button>
        )}
      </div>
      {sessions.isPending ? (
        <Spinner />
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {list.map((session) => (
            <SessionRow
              key={session.id}
              session={session}
              pending={revoke.isPending && revoke.variables === session.id}
              onRevoke={() => revoke.mutate(session.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function SessionRow({
  session,
  pending,
  onRevoke,
}: {
  session: ActiveSession;
  pending: boolean;
  onRevoke: () => void;
}) {
  const Icon = icons[session.kind];
  const { t } = useTranslation();
  const details = [
    session.current
      ? t('Este dispositivo')
      : t('Activa {{time}}', { time: formatRelative(session.lastUsedAt) }),
    session.ip,
  ].filter(Boolean);
  return (
    <li className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
      <Icon className="size-5 shrink-0 text-muted" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{session.device}</p>
        <p className="truncate text-xs text-muted">{details.join(' · ')}</p>
      </div>
      {!session.current && (
        <Button variant="secondary" size="sm" loading={pending} onClick={onRevoke}>
          {t('Cerrar', { context: 'session' })}
        </Button>
      )}
    </li>
  );
}
